<template>
  <page-header-wrapper>
    <div class="erp-list-page baidao-list-page pool-config-page">
      <section class="erp-table-card pool-config-shell">
        <div class="erp-table-toolbar">
          <div>
            <h3>公海配置</h3>
            <p>规则按当前经营主体独立保存，领取、分配与自动入池均以服务端配置为准。</p>
          </div>
          <div class="erp-toolbar-actions">
            <span class="erp-record-count">{{ statusText }}</span>
            <a-button icon="redo" :disabled="loading || saving" @click="loadConfig">重新加载</a-button>
            <a-button v-hasPermi="['erp:crm:pool:config']" type="primary" icon="save" :loading="saving" @click="save">保存配置</a-button>
          </div>
        </div>

        <a-spin :spinning="loading">
          <div class="pool-config-body">
            <div class="erp-status-strip">
              <a-tag :color="form.status === 'enabled' ? 'green' : 'default'">{{ form.status === 'enabled' ? '规则启用' : '规则停用' }}</a-tag>
              <span>经营主体 {{ subjectLabel }} · 配置版本 V{{ form.version == null ? '—' : form.version }}</span>
            </div>

            <section class="erp-form-card">
              <div class="erp-section-title"><span class="erp-section-index">01</span><div><h4>入池规则</h4><p>以最近有效跟进、建档或最近出池时间中的最新值作为计时起点</p></div></div>
              <div class="pool-config-grid">
                <label><span>线索未跟进</span><a-input-number v-model="form.leadNoFollowDays" :min="1" :max="3650" /><em>天后自动入池</em></label>
                <label><span>客户未跟进</span><a-input-number v-model="form.customerNoFollowDays" :min="1" :max="3650" /><em>天后自动入池</em></label>
                <label class="switch-field"><span>自动入池</span><a-switch :checked="form.autoPoolEnabled === 1" checked-children="开启" un-checked-children="关闭" @change="value => form.autoPoolEnabled = value ? 1 : 0" /><em>默认关闭，保存后才生效</em></label>
                <label class="switch-field"><span>部门公海</span><a-switch :checked="form.departmentPoolEnabled === 1" @change="value => form.departmentPoolEnabled = value ? 1 : 0" /><em>关闭后统一进入主体公海</em></label>
                <label class="switch-field"><span>配置状态</span><a-switch :checked="form.status === 'enabled'" checked-children="启用" un-checked-children="停用" @change="value => form.status = value ? 'enabled' : 'disabled'" /><em>停用仅暂停自动规则，不删除历史</em></label>
              </div>
            </section>

            <section class="erp-form-card">
              <div class="erp-section-title"><span class="erp-section-index">02</span><div><h4>领取与保护</h4><p>所有限制均在服务端校验，批量领取仍逐条返回结果</p></div></div>
              <div class="pool-config-grid">
                <label><span>领取保护期</span><a-input-number v-model="form.protectionDays" :min="0" :max="365" /><em>天</em></label>
                <label><span>重复领取冷却</span><a-input-number v-model="form.reclaimCooldownDays" :min="0" :max="3650" /><em>天</em></label>
                <label><span>领取统计周期</span><a-input-number v-model="form.claimLimitDays" :min="1" :max="365" /><em>天</em></label>
                <label><span>周期领取上限</span><a-input-number v-model="form.claimLimitCount" :min="1" :max="10000" /><em>条</em></label>
              </div>
              <a-alert type="info" show-icon message="保护期与冷却期用途不同" description="保护期避免刚领取的对象被立即再次调入公海；冷却期限制原负责人或同一人员反复领取同一对象。" />
            </section>

            <section class="erp-form-card">
              <div class="erp-section-title pool-run-title">
                <span class="erp-section-index">03</span>
                <div><h4>自动执行记录</h4><p>每次扫描保留候选、成功、失败和幂等跳过数，失败对象按重试时间再执行</p></div>
                <a-button
                  v-hasPermi="['erp:crm:pool:config']"
                  type="primary"
                  icon="play-circle"
                  :loading="running"
                  :disabled="loading || saving"
                  @click="runAuto">立即执行</a-button>
              </div>
              <a-table
                row-key="id"
                size="small"
                bordered
                :columns="runColumns"
                :data-source="runs"
                :pagination="false"
                :loading="runLoading">
                <template slot="runStatus" slot-scope="text"><a-tag :color="runStatus(text).color">{{ runStatus(text).text }}</a-tag></template>
                <template slot="runTime" slot-scope="text">{{ formatTime(text) }}</template>
              </a-table>
              <div v-if="!runLoading && !runs.length" class="pool-run-empty">尚无执行记录。开启自动入池并保存后，可立即执行一次验证候选数据。</div>
            </section>

            <section class="erp-form-card">
              <div class="erp-section-title"><span class="erp-section-index">04</span><div><h4>配置说明</h4><p>保存会保留修改前后快照，用于后续审计与问题追溯</p></div></div>
              <a-textarea v-model.trim="form.remark" :rows="4" :max-length="500" placeholder="填写规则调整原因或适用范围" />
              <div class="pool-config-meta">最近更新时间：{{ formatTime(form.updateTime) }}</div>
            </section>
          </div>
        </a-spin>
      </section>
    </div>
  </page-header-wrapper>
</template>

<script>
import { getCrmPoolConfig, updateCrmPoolConfig, runCrmPoolAuto, listCrmPoolAutoRuns } from '@/api/erp/crm'

export default {
  name: 'ErpCrmPoolConfig',
  data () {
    return {
      loading: false,
      saving: false,
      running: false,
      runLoading: false,
      runs: [],
      runColumns: [
        { title: '执行时间', dataIndex: 'startedTime', width: 160, scopedSlots: { customRender: 'runTime' } },
        { title: '触发方式', dataIndex: 'triggerType', width: 100, customRender: value => value === 'manual' ? '手动' : '定时' },
        { title: '候选', dataIndex: 'candidateCount', width: 80, align: 'right' },
        { title: '成功', dataIndex: 'successCount', width: 80, align: 'right' },
        { title: '失败', dataIndex: 'failureCount', width: 80, align: 'right' },
        { title: '跳过', dataIndex: 'skippedCount', width: 80, align: 'right' },
        { title: '状态', dataIndex: 'status', width: 110, scopedSlots: { customRender: 'runStatus' } },
        { title: '运行说明', dataIndex: 'lastError', ellipsis: true, customRender: value => value || '—' }
      ],
      form: this.emptyForm()
    }
  },
  computed: {
    subjectLabel () {
      const value = this.$store.state.user.info && this.$store.state.user.info.operatingSubjectId
      return value == null || Number(value) === 0 ? '默认经营主体' : `#${value}`
    },
    statusText () { return this.form.status === 'enabled' ? '当前规则生效' : '当前规则已停用' }
  },
  created () { this.loadConfig(); this.loadRuns() },
  methods: {
    emptyForm () { return { id: undefined, version: undefined, leadNoFollowDays: 7, customerNoFollowDays: 30, protectionDays: 7, reclaimCooldownDays: 30, claimLimitCount: 20, claimLimitDays: 7, departmentPoolEnabled: 1, autoPoolEnabled: 0, status: 'enabled', remark: '', updateTime: undefined } },
    loadConfig () {
      this.loading = true
      return getCrmPoolConfig().then(res => { this.form = { ...this.emptyForm(), ...(res.data || {}) } }).finally(() => { this.loading = false })
    },
    validate () {
      const fields = [
        ['线索未跟进天数', this.form.leadNoFollowDays, 1], ['客户未跟进天数', this.form.customerNoFollowDays, 1],
        ['领取保护期', this.form.protectionDays, 0], ['重复领取冷却期', this.form.reclaimCooldownDays, 0],
        ['领取统计周期', this.form.claimLimitDays, 1], ['周期领取上限', this.form.claimLimitCount, 1]
      ]
      const invalid = fields.find(item => !Number.isInteger(Number(item[1])) || Number(item[1]) < item[2])
      if (invalid) { this.$message.warning(`${invalid[0]}填写不正确`); return false }
      return true
    },
    save () {
      if (!this.validate()) return
      this.saving = true
      updateCrmPoolConfig(this.form).then(res => { this.form = { ...this.form, ...(res.data || {}) }; this.$message.success('公海配置已保存') }).finally(() => { this.saving = false })
    },
    loadRuns () {
      this.runLoading = true
      return listCrmPoolAutoRuns().then(res => { this.runs = res.data || [] }).finally(() => { this.runLoading = false })
    },
    runAuto () {
      if (this.form.autoPoolEnabled !== 1 || this.form.status !== 'enabled') {
        this.$message.warning('请先开启自动入池和配置状态，并保存配置')
        return
      }
      this.running = true
      runCrmPoolAuto().then(res => {
        const run = res.data || {}
        this.$message.success(`扫描完成：候选 ${run.candidateCount || 0} 条，成功 ${run.successCount || 0} 条，失败 ${run.failureCount || 0} 条`)
        return this.loadRuns()
      }).finally(() => { this.running = false })
    },
    runStatus (value) {
      return {
        success: { color: 'green', text: '成功' },
        partial_failed: { color: 'orange', text: '部分失败' },
        failed: { color: 'red', text: '失败' },
        running: { color: 'blue', text: '执行中' }
      }[value] || { color: 'default', text: value || '—' }
    },
    formatTime (value) { return value ? String(value).replace('T', ' ').slice(0, 16) : '—' }
  }
}
</script>

<style scoped>
.pool-config-shell{max-width:1180px;margin:0 auto}.pool-config-body{padding:0 20px 22px}.pool-config-body .erp-status-strip{margin:18px 0}.pool-config-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:18px 28px;margin-bottom:18px}.pool-config-grid label{display:grid;grid-template-columns:132px 150px minmax(0,1fr);align-items:center;min-height:38px}.pool-config-grid label>span{color:#334155;font-weight:500}.pool-config-grid .ant-input-number{width:140px}.pool-config-grid em{color:#7a8798;font-size:12px;font-style:normal}.pool-config-grid .switch-field{grid-template-columns:132px 72px minmax(0,1fr)}.pool-config-meta{margin-top:10px;color:#7a8798;font-size:12px;text-align:right}.pool-run-title{display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:center}.pool-run-empty{padding:14px 2px 2px;color:#7a8798;font-size:12px}@media(max-width:900px){.pool-config-grid{grid-template-columns:1fr}.pool-config-shell{max-width:none}.pool-run-title{grid-template-columns:auto minmax(0,1fr)}.pool-run-title .ant-btn{grid-column:2;margin-top:10px;justify-self:start}}@media(max-width:560px){.pool-config-grid label,.pool-config-grid .switch-field{grid-template-columns:1fr;gap:7px}.pool-config-body{padding:0 12px 18px}}
</style>
